import type { PayeeView } from "../api/types.js";
import { clock, dateTime, sgDay, statementDate } from "../lib/format.js";

/**
 * 17 Payees (DCTA-111 run 2; UX spec §E.4/§E.6/§E.7, mock-up 17-payees.html) from `GET /v1/payees`.
 *
 *  - A nickname is the customer's own text and is carried as text, whole: the views draw it with
 *    textContent, so markup or an instruction in it stays inert (the seed's `pay_inj001`).
 *  - Every row carries the name the bank holds and the last digits, so two "Mum"s are told apart.
 *  - Your own accounts are not payees (they are on Home).
 *  - A payee in its 12-hour wait says when payments can go out — the bank's `cooling_until`.
 *
 * Not here — no read in the lab yet (backend asks): when they were last paid and how much (ASK-12),
 * rename / remove / add (ASK-12, ASK-14, ASK-13), and the firewall's word on an odd nickname
 * (the deck's "This nickname has unusual text in it" needs a flag only the bank can give).
 */
export interface PayeeRow {
  readonly id: string;
  readonly initial: string;
  /** The customer's word for them, or the name the bank holds when there is none. */
  readonly name: string;
  readonly sub: string;
  /** "From 07:11 tomorrow" while a new payee's wait runs. */
  readonly flag?: string;
  /** What search reads: both names and the digits, lower case. */
  readonly haystack: string;
}

export interface PayeesModel {
  /** Added recently and still new, newest first. */
  readonly fresh: readonly PayeeRow[];
  /** Everyone else, by name. */
  readonly all: readonly PayeeRow[];
}

export interface PayeeDetail {
  readonly initial: string;
  readonly name: string;
  readonly registered: string;
  readonly kv: readonly (readonly [string, string])[];
  readonly wait?: { readonly title: string; readonly sub: string };
}

const RAIL: Readonly<Record<string, string>> = { mobile: "PayNow mobile", uen: "PayNow UEN", nric: "PayNow NRIC", account_number: "Account" };

/** "+65 •••• 3321" → "···3321"; "•••• 345K" → "···345K" — the bank's own trailing characters, never re-masked. */
function tail(masked: string): string {
  const m = /([0-9A-Za-z]{2,})\s*$/.exec(masked);
  return m === null ? masked : `···${m[1] as string}`;
}

const rail = (p: PayeeView): string => `${RAIL[p.proxy_type] ?? "Paid by"} ${tail(p.masked_proxy)}`;
const nameOf = (p: PayeeView): string => (p.nickname !== undefined && p.nickname.trim() !== "" ? p.nickname : p.registered_name);
export const initialOf = (name: string): string => [...name.trim()][0]?.toUpperCase() ?? "·";

/** When a wait ends, in the bank's time: "07:11", "07:11 tomorrow", "7 Oct 07:11". */
function until(iso: string, nowMs: number): string {
  if (sgDay(iso) === sgDay(new Date(nowMs).toISOString())) return clock(iso);
  if (sgDay(iso) === sgDay(new Date(nowMs + 86_400_000).toISOString())) return `${clock(iso)} tomorrow`;
  return dateTime(iso);
}

const waiting = (p: PayeeView, nowMs: number): string | undefined =>
  p.cooling_until !== undefined && Date.parse(p.cooling_until) > nowMs ? until(p.cooling_until, nowMs) : undefined;

function row(p: PayeeView, nowMs: number): PayeeRow {
  const name = nameOf(p);
  const hasNick = name !== p.registered_name;
  const addedToday = sgDay(p.added_at) === sgDay(new Date(nowMs).toISOString());
  const base = hasNick ? `${p.registered_name} ${tail(p.masked_proxy)}` : rail(p);
  const from = waiting(p, nowMs);
  return {
    id: p.payee_id,
    initial: initialOf(name),
    name,
    sub: p.is_new_payee && addedToday ? `${base} · Added today` : base,
    ...(from === undefined ? {} : { flag: `From ${from}` }),
    haystack: `${name} ${p.registered_name} ${tail(p.masked_proxy).replace(/^···/, "")}`.toLowerCase(),
  };
}

export function payeesModel(payees: readonly PayeeView[], nowMs: number): PayeesModel {
  const theirs = payees.filter((p) => p.is_own_account !== true);
  const fresh = theirs.filter((p) => p.is_new_payee).sort((a, b) => b.added_at.localeCompare(a.added_at));
  const all = theirs
    .filter((p) => !p.is_new_payee)
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b), "en", { sensitivity: "base" }));
  return { fresh: fresh.map((p) => row(p, nowMs)), all: all.map((p) => row(p, nowMs)) };
}

/** Search by nickname, registered name or digits (§E.1): any part, any case; "···3321" and "3321" alike. */
export function searchPayees(rows: readonly PayeeRow[], query: string): readonly PayeeRow[] {
  const q = query.trim().toLowerCase().replace(/^[·.•\s]+/, "");
  if (q === "") return rows;
  return rows.filter((r) => r.haystack.includes(q));
}

export function payeeDetail(p: PayeeView, nowMs: number): PayeeDetail {
  const name = nameOf(p);
  const today = sgDay(p.added_at) === sgDay(new Date(nowMs).toISOString());
  const added = today ? `Today, ${clock(p.added_at)}` : `${statementDate(p.added_at)} ${sgDay(p.added_at).slice(0, 4)}`;
  const from = waiting(p, nowMs);
  return {
    initial: initialOf(name),
    name,
    registered: p.registered_name,
    kv: [
      ["Name at the bank", p.registered_name],
      ["Paid by", rail(p)],
      ["Added", added],
    ],
    ...(from === undefined
      ? {}
      : { wait: { title: `Payments can go out from ${from}`, sub: "Every new payee has a 12-hour wait. Pay now and it waits until then." } }),
  };
}
